// Px3 Pipeline - typed browser client for /api/px3/admin.
//
// Every shape below is derived from the router (src/routes/px3-admin.ts) and the
// repo layer (src/services/database/repos/px3.ts) plus src/lib/px3-progress.ts.
// The router is the contract; the design doc is not.
//
// SECURITY: an order borrows four customer secrets (registrar key, Cloudflare
// token, sequencer key, MasterInbox key), a callback token, and one password
// plus one photo per mailbox. None of them can reach this file by accident:
// ORDER_SELECT / MAILBOX_SELECT in the repo carry only `has_*` booleans and the
// `secrets_purged_at` timestamp, which is metadata about a secret and never a
// secret. No type here has a field that holds one, and none should ever be
// given one.
//
// The single exception is deliberate and narrow: getMailboxPassword hits the
// dedicated decrypt-on-demand endpoint, because the house convention is that an
// operator view shows a mailbox password in cleartext (spec 5.8). It returns a
// bare value that no other type embeds, so nothing can spread it into a row.
//
// Timestamps arrive as ISO strings.

import { ApiError, api } from '../api/client';

export type { ApiError } from '../api/client';

const BASE = '/api/px3/admin';

/* ─── Types ─────────────────────────────────────────────────────────────── */

/**
 * Order lifecycle (spec 5.1). `queued -> running -> completed` is the happy
 * path; the rest are side states an operator has to read:
 * `awaiting_capacity` (no free seats, the scheduler retries every 10 min),
 * `needs_attention` (a stage finished with failures, waiting on an action),
 * `failed` (preflight rejected it), `cancelled` (operator abort after work
 * started), `dry_run_complete` (terminal for dry runs).
 */
export type Px3OrderStatus =
  | 'queued'
  | 'running'
  | 'awaiting_capacity'
  | 'needs_attention'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'dry_run_complete';

/** The 12 stages, in the order the machine walks them. Mirrors PX3_STAGE_ORDER
 *  server-side; the rail draws exactly this many tiles. */
export type Px3Stage =
  | 'preflight'
  | 'dns_route'
  | 'claim'
  | 'dns_activate'
  | 'dns_records'
  | 'domain_add'
  | 'rename'
  | 'photos'
  | 'imaps'
  | 'sequencer'
  | 'masterinbox'
  | 'done';

/** Per-item outcome. `abandoned` is what "Continue anyway" leaves behind: the
 *  item was never built and counts as failed in every later report. */
export type Px3ItemStatus = 'pending' | 'running' | 'error' | 'abandoned' | 'done';

/** A portal that may POST orders. `api_key_prefix` is the public half of the
 *  key; the row stores a sha256 of the whole thing and nothing can read it back. */
export interface Px3Source {
  id: string;
  name: string;
  api_key_prefix: string;
  created_by: string | null;
  created_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
}

export interface Px3Order {
  id: string;
  source_id: string | null;
  /** null when the order was submitted from this dashboard rather than a portal. */
  source_name: string | null;
  external_ref: string;
  customer: string;
  status: Px3OrderStatus;
  stage: Px3Stage;
  dry_run: boolean;
  users_per_domain: number;
  domains_total: number;
  mailboxes_total: number;
  registrar_provider: string;
  has_registrar_key: boolean;
  dns_provider: string;
  cf_account_id: string;
  /** The posted Cloudflare token is still on the order. It only carries the
   *  token from intake to preflight, which copies it onto the cloudflare_accounts
   *  row - `false` on a running order is normal, not a missing credential. */
  has_cf_token: boolean;
  cf_account_row_id: number | null;
  forwarding_target_url: string | null;
  sequencer_provider: string;
  has_sequencer_key: boolean;
  sequencer_profile: string;
  sequencer_settings: unknown;
  sequencer_run_id: string | null;
  /** "The order carries a MasterInbox block", NOT "the MI key is still held" -
   *  it survives the secrets purge, so never read it as a credential check. */
  has_masterinbox: boolean;
  masterinbox_workspace_id: number | null;
  masterinbox_app_id: string | null;
  masterinbox_trust_run_id: string | null;
  callback_url: string | null;
  has_callback_token: boolean;
  callback_seq: number;
  /** The preflight report, free-form JSON. Rendered as a <pre>, never parsed. */
  preflight: unknown | null;
  error_code: string | null;
  error_message: string | null;
  attention_reason: string | null;
  stage_started_at: string | null;
  secrets_purged_at: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  /** List endpoint only - the detail endpoint leaves them undefined rather than
   *  reporting a zero it did not compute. */
  mailboxes_done?: number;
  mailboxes_failed?: number;
}

export interface Px3OrderDomain {
  order_id: string;
  domain: string;
  position: number;
  panel_email: string | null;
  cf_zone_id: string | null;
  ns1: string | null;
  ns2: string | null;
  zone_created_at: string | null;
  ns_set_at: string | null;
  zone_active_at: string | null;
  records_written_at: string | null;
  forwarding_written_at: string | null;
  google_added_at: string | null;
  google_verified_at: string | null;
  dkim_at: string | null;
  dkim_status: string | null;
  /** Records that were already present and did not match ours. Free-form. */
  conflicts: unknown | null;
  status: Px3ItemStatus;
  error_stage: Px3Stage | null;
  error_code: string | null;
  error_message: string | null;
  attempts: number;
  updated_at: string;
}

export interface Px3OrderMailbox {
  order_id: string;
  email: string;
  domain: string;
  position: number;
  panel_email: string | null;
  /** Where the seat was claimed before the rename. Non-null from `claim` on. */
  holding_email: string | null;
  first_name: string;
  last_name: string;
  group_label: string | null;
  photo_mime: string | null;
  /** Inline base64 is still held. A photo_url-only mailbox reports FALSE, and it
   *  goes false again after the secrets purge - so it is NOT "has a picture". */
  has_photo: boolean;
  photo_url: string | null;
  photo_status: 'pending' | 'set' | 'none' | 'error';
  claimed_at: string | null;
  licensed_at: string | null;
  renamed_at: string | null;
  alias_stripped_at: string | null;
  photo_at: string | null;
  imaps_pushed_at: string | null;
  login_verified_at: string | null;
  sequencer_connected_at: string | null;
  masterinbox_connected_at: string | null;
  sequencer_status: string | null;
  sequencer_error: string | null;
  masterinbox_error: string | null;
  status: Px3ItemStatus;
  error_stage: Px3Stage | null;
  error_code: string | null;
  error_message: string | null;
  updated_at: string;
}

export interface Px3Event {
  id: number;
  order_id: string;
  ts: string;
  stage: string | null;
  level: 'info' | 'warn' | 'error';
  message: string;
  meta: unknown | null;
}

/** One rail tile. Computed server-side from the SAME isDone predicates the
 *  reconciler uses, so the rail and the queue never disagree. */
export interface Px3StageProgress {
  name: Px3Stage;
  state: 'pending' | 'running' | 'done' | 'failed' | 'skipped';
  done: number;
  failed: number;
  total: number;
}

/** GET /orders/:id */
export interface Px3OrderDetail {
  order: Px3Order;
  domains: Px3OrderDomain[];
  mailboxes: Px3OrderMailbox[];
  progress: Px3StageProgress[];
}

export interface Px3Profile {
  name: string;
  provider: string;
  settings: Record<string, unknown>;
  tags: string[];
  group_tags: boolean;
  tracking: boolean;
}

export interface Px3Exclusion {
  panel_email: string;
  reason: string | null;
  created_at: string;
}

/**
 * GET /pool. `occupancy_histogram` is keyed by the router's own bucket labels,
 * `unknown` included - a panel that has never been probed is not "full", it is
 * unread, and the allocator re-probes before it trusts any of these.
 */
export interface Px3Pool {
  eligible: number;
  occupancy_histogram: Record<string, number>;
  exclusions: Px3Exclusion[];
}

/** The seven buckets the router emits, in display order. Kept here so the view
 *  paints them all - including the ones that are zero, which is information. */
export const PX3_HISTOGRAM_BUCKETS: readonly string[] = [
  '0',
  '1-2',
  '3-5',
  '6-11',
  '12-17',
  '18',
  'unknown',
];

/** GET /queue. `px3` is BullMQ's raw getJobCounts for the px3 queue; `panel` is
 *  a count of jobs on the SHARED panel queue by name prefix, so sequencer and
 *  oauth work that competes for the same slots is visible next to ours. */
export interface Px3QueueStats {
  px3: Record<string, number>;
  panel: Record<string, number>;
}

/** POST /sources - the only moment the key exists in cleartext anywhere. If the
 *  operator loses it the fix is a new source, not a lookup. */
export interface Px3CreatedSource {
  source: Px3Source;
  api_key: string;
}

/** POST /orders (the admin tester). Mirrors summarize() in px3-intake.ts;
 *  `duplicate` is present and true when the (source, external_ref) pair already
 *  existed, in which case NOTHING was created and the existing order is named. */
export interface Px3SubmitResult {
  order_id: string;
  external_ref: string;
  status: Px3OrderStatus;
  stage: Px3Stage;
  domains: number;
  mailboxes: number;
  users_per_domain: number;
  duplicate?: boolean;
}

/** One `{path, message}` from a rejected payload. `details[]` on invalid_payload
 *  and domain_in_flight; the submit form prints them line by line. */
export interface Px3PayloadIssue {
  path: string;
  message: string;
}

/** The six operator actions (spec 5.7), as the router spells them in the path. */
export type Px3Action =
  | 'retry-failed'
  | 'continue'
  | 'abort'
  | 'resend-callback'
  | 'rerun-preflight'
  | 'purge-secrets';

/** What each action answers with. A union rather than `unknown` so a caller can
 *  report the real counts back to the operator instead of "done". */
export interface Px3ActionResult {
  /** retry-failed / continue: rows moved back to pending, or abandoned. */
  domains?: number;
  mailboxes?: number;
  /** abort */
  aborted?: boolean;
  /** resend-callback: which callback event was queued. */
  event?: string;
  /** rerun-preflight */
  queued?: boolean;
  /** purge-secrets */
  purged?: boolean;
}

/**
 * The error codes this surface can answer with, for reference. Nothing here
 * branches on them - every view prints the server's message verbatim - so this
 * is documentation, not a type.
 *
 * From the px3 routers (the same codes the intake contract publishes):
 * invalid_id           400  the order/source id in the path is not a uuid
 * not_found            404  no such order or source
 * unknown_action       400  action name not one of the six
 * action_refused       409  the engine refused it in this status (re-running
 *                           preflight on a running order, for one). The
 *                           operator's mistake, not a server fault
 * invalid_name         400  source name empty or over 100 chars
 * invalid_payload      400  submit payload failed validation; the error carries
 *                           details[], read with px3PayloadIssues
 * unsupported_provider 400  registrar/dns/sequencer provider not in v1
 * domain_in_flight     409  a domain is already in another live order
 * payload_too_large    413  body over 60 MB
 *
 * From the shared middleware and client:
 * INTERNAL_ERROR       500  an unhandled throw (middleware/error-handler.ts)
 * UNAUTHORIZED         401  session expired (auth middleware, before the router)
 * PARSE_ERROR            -  response body was not JSON (client.ts)
 */

/* ─── Transport ─────────────────────────────────────────────────────────── */

/**
 * Thin wrapper over the shared client so every call in this module goes through
 * one place. `api` unwraps `{ success: true, data }` and throws `ApiError` with
 * `.code`, `.status` and `.data` on `{ success: false, error }`.
 */
async function req<T>(path: string, init?: RequestInit): Promise<T> {
  return api<T>(`${BASE}${path}`, init);
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return req<T>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

const enc = encodeURIComponent;

/**
 * Submit goes through its own fetch rather than `api`, for one reason: the
 * shared client copies `body.data` onto the thrown ApiError, and a rejected
 * payload puts its `details[]` at `body.error.details` (that placement is fixed
 * by docs/px3-intake-contract.md, which is published to people integrating
 * against us). Routed through `api`, every "which field is wrong" line would be
 * silently dropped and the form could only ever say "Payload failed validation".
 *
 * The thrown value is the same ApiError with the same code, status and message,
 * carrying `details[]` as `.data` - so px3ErrorMessage and px3PayloadIssues read
 * it exactly like any other failure from this module.
 */
async function submitRaw(path: string, payload: unknown): Promise<Px3SubmitResult> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  let body: {
    success?: boolean;
    data?: unknown;
    error?: { code?: string; message?: string; details?: unknown };
  };
  try {
    body = await res.json();
  } catch {
    throw new ApiError('PARSE_ERROR', 'Failed to parse response', res.status);
  }
  if (!res.ok || body.success === false) {
    throw new ApiError(
      body.error?.code ?? 'UNKNOWN',
      body.error?.message ?? 'Request failed',
      res.status,
      body.error?.details,
    );
  }
  return body.data as Px3SubmitResult;
}

/** Operator-facing text for any thrown value. Server messages here are built
 *  from row data and validation paths, never from a borrowed credential. */
export function px3ErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Request failed';
}

/**
 * The `details[]` a rejected submit carries, or an empty list.
 *
 * Shape-checked rather than cast: `ApiError.data` is whatever the server put in
 * the envelope, and the submit form prints these straight to screen. A cast
 * would render `undefined: undefined` for a response that carried something
 * else.
 */
export function px3PayloadIssues(err: unknown): Px3PayloadIssue[] {
  if (!(err instanceof ApiError) || !Array.isArray(err.data)) return [];
  return err.data.filter(
    (row): row is Px3PayloadIssue =>
      typeof row === 'object' &&
      row !== null &&
      typeof (row as Px3PayloadIssue).path === 'string' &&
      typeof (row as Px3PayloadIssue).message === 'string',
  );
}

/* ─── Endpoints ─────────────────────────────────────────────────────────── */

export const px3Api = {
  /** GET /orders - newest first. `status` filters to those statuses; an empty
   *  list means no filter, which is what the router does with an empty param. */
  listOrders: (opts: { status?: Px3OrderStatus[]; limit?: number } = {}): Promise<Px3Order[]> => {
    const params = new URLSearchParams();
    if (opts.status && opts.status.length > 0) params.set('status', opts.status.join(','));
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return req<Px3Order[]>(`/orders${qs ? `?${qs}` : ''}`);
  },

  /** GET /orders/:id - the order, both item tables and the 12-tile rail. */
  getOrder: (orderId: string): Promise<Px3OrderDetail> =>
    req<Px3OrderDetail>(`/orders/${enc(orderId)}`),

  /** GET /orders/:id/events - newest first, server-capped at 1000. */
  listEvents: (orderId: string, limit = 200): Promise<Px3Event[]> =>
    req<Px3Event[]>(`/orders/${enc(orderId)}/events?limit=${limit}`),

  /**
   * GET /orders/:id/mailboxes/:email/password - decrypts on demand.
   *
   * `password` is null once the order's secrets are purged, which is the normal
   * end state of every finished order. The caller must show that as "purged",
   * not as an empty password.
   */
  getMailboxPassword: (orderId: string, email: string): Promise<{ password: string | null }> =>
    req<{ password: string | null }>(`/orders/${enc(orderId)}/mailboxes/${enc(email)}/password`),

  /**
   * POST /orders/:id/actions/:action - the six operator actions, all logged as
   * events server-side.
   * Errors: not_found, unknown_action, action_refused (409, the engine's own
   * status gate), invalid_id.
   */
  action: (orderId: string, action: Px3Action): Promise<Px3ActionResult> =>
    post<Px3ActionResult>(`/orders/${enc(orderId)}/actions/${action}`),

  /**
   * POST /orders - the admin tester. Goes through the IDENTICAL gate as the
   * portal intake with source null, so a green submit here proves the real path.
   * A repeat of an (admin, external_ref) pair answers 200 with
   * `duplicate: true` and creates NOTHING, so the caller must read that flag
   * before telling the operator a new order exists.
   * Errors: invalid_payload / unsupported_provider / domain_in_flight (all
   * carry Px3PayloadIssue[] on the error, see px3PayloadIssues),
   * payload_too_large.
   */
  submitOrder: (payload: unknown): Promise<Px3SubmitResult> => submitRaw('/orders', payload),

  /** GET /sources - newest first, revoked ones included. */
  listSources: (): Promise<Px3Source[]> => req<Px3Source[]>('/sources'),

  /** POST /sources - answers with the key IN CLEARTEXT, once and only once. The
   *  caller must show it and must not expect to fetch it again. */
  createSource: (name: string): Promise<Px3CreatedSource> =>
    post<Px3CreatedSource>('/sources', { name }),

  /** POST /sources/:id/revoke - idempotent; re-revoking keeps the first stamp. */
  revokeSource: (sourceId: string): Promise<{ revoked: boolean }> =>
    post<{ revoked: boolean }>(`/sources/${enc(sourceId)}/revoke`),

  /** GET /pool - eligible panel count, the free-seat histogram and the
   *  exclusion list, in one read. */
  getPool: (): Promise<Px3Pool> => req<Px3Pool>('/pool'),

  /** POST /pool/exclusions - `added` counts rows actually inserted; re-adding an
   *  existing exclusion is a no-op that keeps the original reason and author. */
  addExclusions: (emails: string[], reason: string): Promise<{ added: number }> =>
    post<{ added: number }>('/pool/exclusions', { emails, reason }),

  /** DELETE /pool/exclusions/:email */
  removeExclusion: (email: string): Promise<{ removed: boolean }> =>
    req<{ removed: boolean }>(`/pool/exclusions/${enc(email)}`, { method: 'DELETE' }),

  /** POST /pool/refresh - enqueues one live occupancy probe across the pool.
   *  The job id is fixed server-side, so two clicks are one probe. */
  refreshPool: (): Promise<{ queued: boolean }> => post<{ queued: boolean }>('/pool/refresh'),

  /** GET /profiles - the named sequencer profiles an order may reference. */
  listProfiles: (): Promise<Px3Profile[]> => req<Px3Profile[]>('/profiles'),

  /** GET /queue - BullMQ counts for px3 plus the panel-queue job mix. */
  getQueue: (): Promise<Px3QueueStats> => req<Px3QueueStats>('/queue'),
};
